import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { PatientService, sharedApiClient } from '../fetcher';
import { useNotifications, medPromptBrowserTitle } from '@medprompt/shared';
import { hasPatientCodesData } from '../detail/PatientDiaryPanel';

const PATIENT_FROM_SEARCH_KEY = 'patientFromSearch';
const PATIENT_FROM_TABLE_KEY = 'patientFromTable';
const PATIENT_FROM_DIARY_KEY = 'patientFromDiary';

const usePatientRecord = ({ nhsId, location, navigate }) => {
  const [searchParams] = useSearchParams();
  const { addNotification } = useNotifications();
  const patientService = useMemo(() => new PatientService(sharedApiClient), []);

  const addNotificationRef = useRef(addNotification);
  useEffect(() => {
    addNotificationRef.current = addNotification;
  }, [addNotification]);

  const [loading, setLoading] = useState(true);
  const [patient, setPatient] = useState(() => location.state?.patient || null);
  const patientId = searchParams.get('patient_id') || patient?.patientId || null;
  const [patientRecord, setPatientRecord] = useState(null);
  const [refreshingPatientCodes, setRefreshingPatientCodes] = useState(false);
  const refreshingPatientCodesRef = useRef(false);
  const nhsIdRef = useRef(nhsId);
  const patientIdRef = useRef(patientId);
  const [fromContext, setFromContext] = useState(null);
  const [returnPath, setReturnPath] = useState(null);

  useEffect(() => {
    nhsIdRef.current = nhsId;
    patientIdRef.current = patientId;
  }, [nhsId, patientId]);

  useEffect(() => {
    const stateFromContext = location.state?.fromContext;
    const stateReturnPath = location.state?.returnPath;

    if (stateFromContext) {
      setFromContext(stateFromContext);
      if (stateFromContext === 'search') {
        sessionStorage.setItem(PATIENT_FROM_SEARCH_KEY, 'true');
      } else if (stateFromContext === 'searchResults') {
        sessionStorage.setItem(PATIENT_FROM_TABLE_KEY, 'true');
      } else if (stateFromContext === 'diary') {
        sessionStorage.setItem(PATIENT_FROM_DIARY_KEY, stateReturnPath || 'true');
        setReturnPath(stateReturnPath);
      }
    } else {
      if (sessionStorage.getItem(PATIENT_FROM_DIARY_KEY)) {
        setFromContext('diary');
        const storedPath = sessionStorage.getItem(PATIENT_FROM_DIARY_KEY);
        if (storedPath && storedPath !== 'true') {
          setReturnPath(storedPath);
        }
      } else if (sessionStorage.getItem(PATIENT_FROM_TABLE_KEY)) {
        setFromContext('searchResults');
      } else if (sessionStorage.getItem(PATIENT_FROM_SEARCH_KEY)) {
        setFromContext('search');
      }
    }
    // Only run on mount/nhsId change - location.state reference changes shouldn't re-trigger
  }, [nhsId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    refreshingPatientCodesRef.current = false;
    setRefreshingPatientCodes(false);
    if (!nhsId || !patientId) {
      setLoading(false);
      return;
    }
    const loadData = async () => {
      setLoading(true);
      try {
        const record = await patientService.fetchPatientRecord(nhsId, patientId);
        if (nhsId !== nhsIdRef.current || patientId !== patientIdRef.current) return;
        setPatientRecord(record);
        if (record?.patient) {
          setPatient(record.patient);
          document.title = medPromptBrowserTitle(record.patient.name || 'Patient');
        }
      } catch (error) {
        if (nhsId !== nhsIdRef.current || patientId !== patientIdRef.current) return;
        console.error('Error loading patient record:', error);
        addNotificationRef.current('patient-load-error', 'Failed to load patient data', { isError: true });
      } finally {
        if (nhsId === nhsIdRef.current && patientId === patientIdRef.current) {
          setLoading(false);
        }
      }
    };
    loadData();
  }, [nhsId, patientId, patientService]);

  const handleRefreshPatientCodes = useCallback(async () => {
    if (!nhsId || !patientId || refreshingPatientCodesRef.current) return;
    const requestedNhsId = nhsId;
    const requestedPatientId = patientId;
    refreshingPatientCodesRef.current = true;
    setRefreshingPatientCodes(true);
    try {
      const codes = await patientService.fetchFreshPatientCodes(requestedNhsId, requestedPatientId);
      if (requestedNhsId !== nhsIdRef.current || requestedPatientId !== patientIdRef.current) return;
      if (!hasPatientCodesData(codes)) {
        addNotificationRef.current('patient-codes-refresh-empty', 'Could not refresh problems from EMIS', { isError: true });
        return;
      }
      setPatientRecord(previous => (
        previous ? { ...previous, patientCodesData: codes } : previous
      ));
    } catch (error) {
      if (requestedNhsId !== nhsIdRef.current || requestedPatientId !== patientIdRef.current) return;
      console.error('Error refreshing patient codes:', error);
      addNotificationRef.current('patient-codes-refresh-error', 'Failed to refresh problems from EMIS', { isError: true });
    } finally {
      refreshingPatientCodesRef.current = false;
      if (requestedNhsId === nhsIdRef.current && requestedPatientId === patientIdRef.current) {
        setRefreshingPatientCodes(false);
      }
    }
  }, [nhsId, patientId, patientService]);

  const handleBack = useCallback(() => {
    sessionStorage.removeItem(PATIENT_FROM_SEARCH_KEY);
    sessionStorage.removeItem(PATIENT_FROM_TABLE_KEY);
    sessionStorage.removeItem(PATIENT_FROM_DIARY_KEY);

    if (fromContext === 'diary' && returnPath) {
      navigate(returnPath);
    } else {
      navigate('/', {
        state: {
          selectTab: 'patients',
          ...(fromContext === 'searchResults' && { preserveSearch: true })
        }
      });
    }
  }, [fromContext, returnPath, navigate]);

  const handleCopyToClipboard = useCallback((text) => {
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(text).then(() => {
        addNotificationRef.current(`copy-success-${Date.now()}`, 'Copied to clipboard');
      }).catch(() => {
        addNotificationRef.current(`copy-error-${Date.now()}`, 'Failed to copy', { isError: true });
      });
    }
  }, []);

  const backLabel = useMemo(() => {
    if (fromContext === 'diary') {
      if (returnPath && returnPath.includes('/consultation-detail/')) {
        return 'Back to Consultation';
      }
      return 'Back to Document';
    }
    if (fromContext === 'searchResults') return 'Back to Search Results';
    return 'Back to Patient Search';
  }, [fromContext, returnPath]);

  return {
    patientService,
    loading,
    patient,
    patientId,
    patientRecord,
    refreshingPatientCodes,
    handleRefreshPatientCodes,
    handleBack,
    handleCopyToClipboard,
    backLabel,
    addNotificationRef,
  };
};

export default usePatientRecord;
